// Journey live-control orchestration. Focused hooks separately own status reads/polling, the shared
// one-shot mutation fence, and each closed mutation. Components never construct control-plane payloads.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { Dispatch, RefObject, SetStateAction } from 'react';
import type {
  JourneyLiveApi,
  JourneyLiveArmMode,
  JourneyLiveRunReceiptView,
  JourneyLiveStatusView,
} from './journey-live-api.ts';

export const JOURNEY_LIVE_ARMED_POLL_MS = 5_000;
export type JourneyLiveOperation = 'preflight' | 'arm' | 'start' | 'stop';

export interface JourneyLiveController {
  readonly status: JourneyLiveStatusView | null;
  readonly loading: boolean;
  readonly operation: JourneyLiveOperation | null;
  readonly error: string | null;
  readonly refresh: () => Promise<void>;
  readonly preflight: (mode: JourneyLiveArmMode) => Promise<void>;
  readonly arm: (mode: JourneyLiveArmMode) => Promise<void>;
  readonly start: (scenarioId: string) => Promise<JourneyLiveRunReceiptView | undefined>;
  readonly stop: () => Promise<void>;
}

type StatusSetter = Dispatch<SetStateAction<JourneyLiveStatusView | null>>;

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message.trim().length > 0 ? error.message : fallback;
}

function useArmedStatusPolling(
  status: JourneyLiveStatusView | null,
  operation: JourneyLiveOperation | null,
  loadStatus: (showLoading: boolean) => Promise<void>,
): void {
  useEffect(() => {
    if (status?.control.state !== 'armed' || operation !== null) return;
    const timer = window.setTimeout(() => void loadStatus(false), JOURNEY_LIVE_ARMED_POLL_MS);
    return () => window.clearTimeout(timer);
  }, [loadStatus, operation, status]);
}

function useInitialStatusLoad(
  loadStatus: (showLoading: boolean) => Promise<void>,
  mountedRef: RefObject<boolean>,
  generationRef: RefObject<number>,
): void {
  useEffect(() => {
    mountedRef.current = true;
    void loadStatus(true);
    return () => {
      mountedRef.current = false;
      generationRef.current += 1;
    };
  }, [generationRef, loadStatus, mountedRef]);
}

function useLiveStatus(api: JourneyLiveApi): {
  readonly status: JourneyLiveStatusView | null;
  readonly setStatus: StatusSetter;
  readonly loading: boolean;
  readonly error: string | null;
  readonly setError: Dispatch<SetStateAction<string | null>>;
  readonly loadStatus: (showLoading: boolean) => Promise<void>;
  readonly invalidateReads: () => void;
  readonly mountedRef: RefObject<boolean>;
} {
  const [status, setStatus] = useState<JourneyLiveStatusView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const generationRef = useRef(0);
  const invalidateReads = useCallback((): void => void (generationRef.current += 1), []);
  const loadStatus = useCallback(
    async (showLoading: boolean): Promise<void> => {
      const generation = ++generationRef.current;
      if (showLoading) setLoading(true);
      try {
        const next = await api.getStatus();
        if (mountedRef.current && generation === generationRef.current) {
          setStatus(next);
          setError(null);
        }
      } catch (cause) {
        if (mountedRef.current && generation === generationRef.current) {
          setError(errorMessage(cause, 'Live control status is unavailable.'));
        }
      } finally {
        if (mountedRef.current && generation === generationRef.current) setLoading(false);
      }
    },
    [api],
  );
  useInitialStatusLoad(loadStatus, mountedRef, generationRef);
  return {
    status,
    setStatus,
    loading,
    error,
    setError,
    loadStatus,
    invalidateReads,
    mountedRef,
  };
}

function useOperationFence(
  mountedRef: RefObject<boolean>,
  invalidateReads: () => void,
): {
  readonly operation: JourneyLiveOperation | null;
  readonly begin: (operation: JourneyLiveOperation) => boolean;
  readonly finish: () => void;
} {
  const [operation, setOperation] = useState<JourneyLiveOperation | null>(null);
  const fencedRef = useRef(false);
  const begin = useCallback(
    (next: JourneyLiveOperation): boolean => {
      if (fencedRef.current) return false;
      fencedRef.current = true;
      invalidateReads();
      setOperation(next);
      return true;
    },
    [invalidateReads],
  );
  const finish = useCallback((): void => {
    fencedRef.current = false;
    if (mountedRef.current) setOperation(null);
  }, [mountedRef]);
  return { operation, begin, finish };
}

function usePreflightMutation(
  api: JourneyLiveApi,
  setStatus: StatusSetter,
  setError: Dispatch<SetStateAction<string | null>>,
  fence: ReturnType<typeof useOperationFence>,
  mountedRef: RefObject<boolean>,
): (mode: JourneyLiveArmMode) => Promise<void> {
  return useCallback(
    async (mode: JourneyLiveArmMode): Promise<void> => {
      if (!fence.begin('preflight')) return;
      setError(null);
      try {
        const next = await api.preflight(mode);
        if (mountedRef.current) {
          setStatus((current) =>
            current === null ? current : { ...current, latestPreflight: next },
          );
        }
      } catch (cause) {
        if (mountedRef.current)
          setError(errorMessage(cause, 'The live preflight did not return a confirmed result.'));
      } finally {
        fence.finish();
      }
    },
    [api, fence.begin, fence.finish, mountedRef, setError, setStatus],
  );
}

function useArmMutation(
  api: JourneyLiveApi,
  status: JourneyLiveStatusView | null,
  setStatus: StatusSetter,
  setError: Dispatch<SetStateAction<string | null>>,
  fence: ReturnType<typeof useOperationFence>,
  mountedRef: RefObject<boolean>,
): (mode: JourneyLiveArmMode) => Promise<void> {
  return useCallback(
    async (mode: JourneyLiveArmMode): Promise<void> => {
      const preflight = status?.latestPreflight;
      if (
        preflight === undefined ||
        !preflight.pass ||
        preflight.mode !== mode ||
        !fence.begin('arm')
      )
        return;
      setError(null);
      try {
        const control = await api.arm(mode, preflight.id);
        if (mountedRef.current)
          setStatus((current) => (current === null ? current : { ...current, control }));
      } catch (cause) {
        if (mountedRef.current)
          setError(errorMessage(cause, 'The live arm did not return a confirmed result.'));
      } finally {
        fence.finish();
      }
    },
    [api, fence.begin, fence.finish, mountedRef, setError, setStatus, status?.latestPreflight],
  );
}

const OPERATOR_STOP_REASON = 'Stopped from the Journey Lab dashboard by an operator.';

function useStopMutation(
  api: JourneyLiveApi,
  setStatus: StatusSetter,
  setError: Dispatch<SetStateAction<string | null>>,
  fence: ReturnType<typeof useOperationFence>,
  mountedRef: RefObject<boolean>,
): () => Promise<void> {
  return useCallback(async (): Promise<void> => {
    if (!fence.begin('stop')) return;
    setError(null);
    try {
      const control = await api.stop(OPERATOR_STOP_REASON);
      if (mountedRef.current)
        setStatus((current) => (current === null ? current : { ...current, control }));
    } catch (cause) {
      if (mountedRef.current)
        setError(errorMessage(cause, 'The emergency stop did not return a confirmed result.'));
    } finally {
      fence.finish();
    }
  }, [api, fence.begin, fence.finish, mountedRef, setError, setStatus]);
}

function useStartMutation(
  api: JourneyLiveApi,
  status: JourneyLiveStatusView | null,
  setError: Dispatch<SetStateAction<string | null>>,
  fence: ReturnType<typeof useOperationFence>,
  mountedRef: RefObject<boolean>,
): (scenarioId: string) => Promise<JourneyLiveRunReceiptView | undefined> {
  return useCallback(
    async (scenarioId: string): Promise<JourneyLiveRunReceiptView | undefined> => {
      const control = status?.control;
      if (
        control?.state !== 'armed' ||
        control.armMode === undefined ||
        control.preflightId === undefined ||
        !fence.begin('start')
      ) {
        return undefined;
      }
      setError(null);
      try {
        return await api.start(
          scenarioId,
          control.armMode,
          control.generation,
          control.preflightId,
        );
      } catch (cause) {
        if (mountedRef.current) {
          setError(
            errorMessage(
              cause,
              'The live start did not return a confirmed outcome. Refresh before trying again.',
            ),
          );
        }
        return undefined;
      } finally {
        fence.finish();
      }
    },
    [api, fence.begin, fence.finish, mountedRef, setError, status?.control],
  );
}

/** Compose status and closed mutations into the render-facing live-control contract. */
export function useJourneyLive(api: JourneyLiveApi): JourneyLiveController {
  const status = useLiveStatus(api);
  const fence = useOperationFence(status.mountedRef, status.invalidateReads);
  useArmedStatusPolling(status.status, fence.operation, status.loadStatus);
  const preflight = usePreflightMutation(
    api,
    status.setStatus,
    status.setError,
    fence,
    status.mountedRef,
  );
  const arm = useArmMutation(
    api,
    status.status,
    status.setStatus,
    status.setError,
    fence,
    status.mountedRef,
  );
  const stop = useStopMutation(api, status.setStatus, status.setError, fence, status.mountedRef);
  const start = useStartMutation(api, status.status, status.setError, fence, status.mountedRef);
  const refresh = useCallback(
    async (): Promise<void> => status.loadStatus(true),
    [status.loadStatus],
  );
  return {
    status: status.status,
    loading: status.loading,
    operation: fence.operation,
    error: status.error,
    refresh,
    preflight,
    arm,
    start,
    stop,
  };
}
